import { cn } from "@/lib/utils";

const INFINITY_PATH =
  "M32 24C40 12 52 12 52 24C52 36 40 36 32 24C24 12 12 12 12 24C12 36 24 36 32 24Z";

/**
 * Proprietary L∞seEnd™ loading indicator: a thin infinity outline with a
 * brighter pulse travelling along the same path. No spinners, no boxes.
 */
export function LooseEndLoadingIndicator({
  size = 56,
  animated = true,
  reducedMotion = false,
  className,
  label = "Loading",
}: {
  size?: number;
  animated?: boolean;
  reducedMotion?: boolean;
  className?: string;
  label?: string;
}) {
  const moving = animated && !reducedMotion;

  return (
    <span
      role="status"
      aria-label={label}
      className={cn("inline-flex shrink-0 items-center justify-center", className)}
      style={{ width: size, height: (size * 48) / 64 }}
    >
      <svg
        viewBox="0 0 64 48"
        width="100%"
        height="100%"
        fill="none"
        aria-hidden="true"
        overflow="visible"
      >
        <defs>
          <filter id="looseend-pulse-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Base stroke — always visible */}
        <path
          d={INFINITY_PATH}
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn("text-primary/25", moving && "looseend-infinity-breathe")}
        />

        {/* Travelling pulse */}
        {moving ? (
          <path
            d={INFINITY_PATH}
            pathLength={100}
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="16 84"
            filter="url(#looseend-pulse-glow)"
            className="text-primary looseend-infinity-pulse"
          />
        ) : null}
      </svg>
    </span>
  );
}
